import React from "react";
import { dataModelFieldSections } from "../../registry/dataModelFieldSections";
import { LeftCluster } from "../SectionToggleRow/SectionToggleRow.styles";
import { FieldLabel, FieldRow, TypeBadge, UsedMark } from "./DataModelFieldRow.styles";
import type { DataModelFieldRowProps } from "./DataModelFieldRow.types";

/**
 * One data-model field in the Data models panel — lettered type badge + name.
 * Optionally a selection toggle and an HTML5 drag source; both behaviours
 * switch on only when their handler is passed.
 */
/** The badge letter is the section toggle's mark for the kind ("#", "ƒ", "D", "Aa", "G"); a time reads as a date. */
const badgeLetter = (kind: DataModelFieldRowProps["kind"]): string =>
  dataModelFieldSections.find((section) => section.fieldIcon === (kind === "time" ? "date" : kind))?.toggleLabel ?? "";

export const DataModelFieldRow: React.FC<DataModelFieldRowProps> = ({
  label,
  kind,
  onHoverStart,
  onHoverEnd,
  describedBy,
  isUsed = false,
  isDragging = false,
  isSelected = false,
  onSelectToggle,
  onDragStart,
  onDragEnd,
  testId,
  className,
}) => (
  <FieldRow
    $depth={0}
    $selected={isSelected}
    $used={isUsed}
    $dragging={isDragging}
    data-dragging={isDragging ? "true" : undefined}
    data-used={isUsed ? "true" : undefined}
    $draggable={Boolean(onDragStart)}
    $toggleable={Boolean(onSelectToggle)}
    data-testid={testId}
    className={className}
    role={onSelectToggle ? "button" : undefined}
    aria-pressed={onSelectToggle ? isSelected : undefined}
    tabIndex={onHoverStart || onSelectToggle ? 0 : undefined}
    aria-describedby={describedBy}
    draggable={onDragStart ? true : undefined}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onClick={onSelectToggle}
    onKeyDown={
      onSelectToggle
        ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onSelectToggle();
            }
          }
        : undefined
    }
    onMouseEnter={onHoverStart ? (event) => onHoverStart(event.currentTarget) : undefined}
    onMouseLeave={onHoverEnd}
    onFocus={onHoverStart ? (event) => onHoverStart(event.currentTarget) : undefined}
    onBlur={onHoverEnd}
  >
    <LeftCluster>
      <TypeBadge $kind={kind} $used={isUsed} $dragging={isDragging} aria-hidden data-testid={`field-type-icon-${kind}`}>
        {badgeLetter(kind)}
      </TypeBadge>
      <FieldLabel $depth={0} $highlighted={false} $used={isUsed} title={label}>
        {label}
      </FieldLabel>
    </LeftCluster>
    {isUsed && <UsedMark size={13} strokeWidth={2.25} aria-label="Used in this chart" role="img" />}
  </FieldRow>
);

export default DataModelFieldRow;
